import type { HeatmapConfig, HeatmapLayoutNode, HeatmapNode } from "./types";
import { computeAreaWeightsUsingSqrt } from "./areaStrategy";

/**
 * Heatmap 布局计算参数。
 * 只依赖基础数据和容器尺寸，不关心具体渲染方式。
 */
export interface ComputeLayoutParams {
  items: HeatmapNode[];
  width: number;
  height: number;
  config?: HeatmapConfig;
}

/**
 * 使用 d3.treemap（若可用）或简单降级算法计算 Heatmap 布局。
 *
 * @description
 * - 有 d3 时：使用 d3.hierarchy + d3.treemap 计算 treemap 布局，色块之间使用 2px 间隙
 * - 无 d3 时：按权重简单从左到右地铺排，保证在 SSR / 单测环境下也能返回合理结果
 *
 * @returns 带有 x / y / width / height 的 HeatmapLayoutNode 数组
 */
export function computeHeatmapLayout(
  params: ComputeLayoutParams,
): HeatmapLayoutNode[] {
  const { items, width, height, config } = params;

  if (!items.length || width <= 0 || height <= 0) {
    return [];
  }

  const weights = computeAreaWeightsUsingSqrt(
    items.map((item) => item.marketCap ?? null),
    config,
  );

  const hasD3 =
    typeof window !== "undefined" && typeof (window as any).d3 !== "undefined";

  if (!hasD3) {
    // 无 d3 时的降级：按权重顺序从左到右、从上到下简单排布
    let offsetX = 0;
    let offsetY = 0;
    const rowHeight = height;

    return items.map((item, index): HeatmapLayoutNode => {
      const ratio = weights[index] ?? 0;
      const area = ratio * width * height;
      const boxWidth = area / rowHeight;

      if (offsetX + boxWidth > width) {
        // 换行
        offsetX = 0;
        offsetY += rowHeight / 2;
      }

      const node: HeatmapLayoutNode = {
        ...item,
        x: offsetX,
        y: offsetY,
        width: boxWidth,
        height: rowHeight / 2,
      };

      offsetX += boxWidth;
      return node;
    });
  }

  const d3 = (window as any).d3 as typeof import("d3");

  // 为 treemap 提供 sum 权重的扩展类型
  type WeightedNode = HeatmapNode & { _weight: number };

  const rawNodes: WeightedNode[] = items.map((item, index) => ({
    ...item,
    _weight: weights[index] ?? 0,
  }));

  // 这里使用 any 简化 d3.hierarchy 的类型推导，避免对 _weight 字段的类型冲突
  const root = d3
    .hierarchy<any>({
      children: rawNodes,
    })
    .sum((d: any) => d._weight || 0);

  const treemap = d3
    .treemap<any>()
    .size([width, height])
    // 色块间隙为 2px
    .paddingInner(2);

  const tree = treemap(root);

  return tree.leaves().map((leaf) => {
    const d = leaf.data as WeightedNode;
    return {
      id: d.id,
      name: d.name,
      marketCap: d.marketCap,
      changePercent24h: d.changePercent24h,
      meta: d.meta,
      x: leaf.x0,
      y: leaf.y0,
      width: leaf.x1 - leaf.x0,
      height: leaf.y1 - leaf.y0,
    };
  });
}
